'use client';

import { Tooltip } from '@pickleballinc/react-ui';
import { clsx } from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { SeasonDivisionPool } from '@/infrastructure/network/dto/SeasonDivisionPool';

interface NavTabsProps {
	teamLeagueId?: string;
	seasonId?: string;
	divisionId?: string;
	activeTeams?: number;
	divisionCount?: number;
	poolId?: string | SeasonDivisionPool;
	isLoadingPoolData?: boolean;
}

export const NavTabs = ({ seasonId, divisionId, activeTeams, divisionCount, poolId, isLoadingPoolData }: NavTabsProps) => {
	const pathname = usePathname();

	const basePath = pathname.split('/team-info')[0];

	const getTabHref = (tabPath: string) => {
		const searchParams = new URLSearchParams();

		if (seasonId) searchParams.set('season', seasonId);
		if (divisionId && divisionId !== 'all') searchParams.set('division', divisionId);

		const queryString = searchParams.toString();
		return queryString ? `${tabPath}?${queryString}` : tabPath;
	};

	const tabs = [
		{
			name: 'Active teams',
			path: `${basePath}/team-info/active-teams`,
			count: activeTeams,
			disabled: false
		},
		{
			name: 'Player activity',
			path: `${basePath}/team-info/player-activity`,
			disabled: false
		},
		{
			name: 'Schedule',
			path: `${basePath}/team-info/scheduler`,
			disabled: !poolId && ((divisionCount && divisionCount > 0) || (divisionId === 'all' && divisionCount === 0))
		}
	];

	return (
		<div className="flex justify-between py-6 sm:justify-normal sm:space-x-4 print-hidden">
			{tabs.map((tab) => {
				const isActive = pathname.includes(tab.path);
				const tabContent = (
					<Link
						key={tab.name}
						replace
						href={getTabHref(tab.path)}
						className={clsx('relative p-2 text-sm font-medium sm:px-4', {
							'cursor-pointer text-darkorange-400': isActive && !tab.disabled,
							'text-gray-500 hover:text-gray-700': !isActive && !tab.disabled,
							'cursor-not-allowed text-gray-400 pointer-events-none': tab.disabled
						})}
					>
						{tab.name}
						{tab.count !== undefined && (
							<span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-800">{tab.count}</span>
						)}
						{isActive && !tab.disabled ? <span className="absolute bottom-0 left-0 h-0.5 w-full bg-darkorange-400" /> : null}
					</Link>
				);

				return tab.disabled ? (
					<Tooltip key={tab.name} placement="bottom">
						<Tooltip.Trigger asChild>
							<p className="mt-[6px] inline-block ">{tabContent}</p>
						</Tooltip.Trigger>
						<Tooltip.Content>
							{isLoadingPoolData ? (
								<p>Loading schedule info...</p>
							) : poolId ? null : divisionId === 'all' && divisionCount && divisionCount > 0 ? (
								<p>Division needs to be selected</p>
							) : !poolId && ((divisionCount && divisionCount > 0) || (divisionId === 'all' && divisionCount === 0)) ? (
								<p>No schedule data available for the selected season and division</p>
							) : null}
						</Tooltip.Content>
					</Tooltip>
				) : (
					tabContent
				);
			})}
		</div>
	);
};
